import axios from 'axios';

const state = {
  isAuthenticated: false,
  admin: null,
  user: null, // For mobile users
  token: localStorage.getItem('authToken') || null,
  userType: localStorage.getItem('userType') || null // 'admin' or 'user'
};

const mutations = {
  SET_AUTH_SUCCESS(state, { admin, token, userType = 'admin' }) {
    state.isAuthenticated = true;
    state.admin = admin;
    state.token = token;
    state.userType = userType;
    localStorage.setItem('authToken', token);
    localStorage.setItem('userType', userType);
    if (admin) {
      localStorage.setItem('adminData', JSON.stringify(admin));
    }
  },
  SET_MOBILE_AUTH_SUCCESS(state, { user, token }) {
    state.isAuthenticated = true;
    state.user = user;
    state.token = token;
    state.userType = 'user';
    localStorage.setItem('authToken', token);
    localStorage.setItem('userType', 'user');
    localStorage.setItem('userData', JSON.stringify(user));
  },
  SET_AUTH_LOGOUT(state) {
    state.isAuthenticated = false;
    state.admin = null;
    state.user = null;
    state.token = null;
    state.userType = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminData');
    localStorage.removeItem('userData');
    localStorage.removeItem('userType');
  },
  SET_ADMIN_DATA(state, admin) {
    state.admin = admin;
    state.isAuthenticated = true;
    state.userType = 'admin';
  },
  SET_USER_DATA(state, user) {
    state.user = user;
    state.isAuthenticated = true;
    state.userType = 'user';
  }
};

const actions = {
  async login({ commit }, credentials) {
    try {
      const response = await axios.post('/auth/login', credentials);
      const { admin, token } = response.data;

      commit('SET_AUTH_SUCCESS', { admin, token });

      return { success: true, message: response.data.message };
    } catch (error) {
      commit('SET_AUTH_LOGOUT');
      const message = error.response?.data?.error || 'Login failed';
      throw new Error(message);
    }
  },

  async verifyToken({ commit, state }) {
    if (!state.token) {
      commit('SET_AUTH_LOGOUT');
      return false;
    }

    try {
      if (state.userType === 'admin') {
        const response = await axios.get('/auth/verify');
        const { admin } = response.data;
        commit('SET_ADMIN_DATA', admin);
      } else if (state.userType === 'user') {
        const response = await axios.get('/mobile/auth/verify');
        const { user } = response.data;
        commit('SET_USER_DATA', user);
      }
      return true;
    } catch (error) {
      commit('SET_AUTH_LOGOUT');
      return false;
    }
  },

  // Mobile authentication actions
  async mobileSendOTP({ commit }, { username, mobileNumber, countryCode }) {
    try {
      const response = await axios.post('/mobile/auth/send-otp', {
        username,
        mobileNumber,
        countryCode
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send OTP';
      throw new Error(message);
    }
  },

  async mobileDirectLogin({ commit }, { mobileNumber, countryCode }) {
    try {
      const response = await axios.post('/mobile/auth/direct-login', {
        mobileNumber,
        countryCode
      });
      const { token, user } = response.data;

      // Store token and user data
      localStorage.setItem('token', token);
      commit('SET_MOBILE_AUTH_SUCCESS', { token, user });

      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      throw new Error(message);
    }
  },

  async mobileVerifyOTP({ commit }, { username, mobileNumber, countryCode, verificationId, code }) {
    try {
      const response = await axios.post('/mobile/auth/verify-otp', {
        username,
        mobileNumber,
        countryCode,
        verificationId,
        code
      });

      const { user, token } = response.data;
      commit('SET_MOBILE_AUTH_SUCCESS', { user, token });

      return response.data;
    } catch (error) {
      commit('SET_AUTH_LOGOUT');
      const message = error.response?.data?.error || 'Failed to verify OTP';
      throw new Error(message);
    }
  },

  async mobileLogin({ commit }, authData) {
    try {
      const { user, token } = authData;
      commit('SET_MOBILE_AUTH_SUCCESS', { user, token });
      return authData;
    } catch (error) {
      commit('SET_AUTH_LOGOUT');
      throw new Error('Mobile login failed');
    }
  },

  logout({ commit }) {
    commit('SET_AUTH_LOGOUT');
  },

  // Initialize auth state from localStorage
  async initializeAuth({ commit, dispatch }) {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    const adminData = localStorage.getItem('adminData');
    const userData = localStorage.getItem('userData');

    if (token && userType) {
      try {
        if (userType === 'admin' && adminData) {
          const admin = JSON.parse(adminData);
          commit('SET_AUTH_SUCCESS', { admin, token, userType });
        } else if (userType === 'user' && userData) {
          const user = JSON.parse(userData);
          commit('SET_MOBILE_AUTH_SUCCESS', { user, token });
        }

        // For now, skip token verification on initialization to prevent logout on refresh
        // Token verification will happen on API calls via the response interceptor
        // This allows users to stay logged in when refreshing the page

      } catch (error) {
        console.error('Auth initialization error:', error);
        commit('SET_AUTH_LOGOUT');
      }
    }
  }
};

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  currentAdmin: state => state.admin,
  currentUser: state => state.user,
  authToken: state => state.token,
  userType: state => state.userType,
  isAdmin: state => state.userType === 'admin',
  isUser: state => state.userType === 'user'
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
